import "../App.scss";

import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  updateUserData,
  updateMainUserData,
  updateDisplayingHealthForm,
  updateDisplayingTermOfUsePage,
  updateUserToken,
} from "../store/userSlice";
import { isLoadChanger } from "../store/mainSlice";
import swalDialog from "../helpers/swalErrorPopup";
import Loader from "./Loader";
import { useNavigate, useLocation } from "react-router-dom";
import userAPI from "../api/userAPI";
import { PARTNER_KEYS } from "../utils/partner";
import { setPartner, setIsOrthodox, setIsMinor } from "../store/partnerStore";

export default function LayoutV2({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoad = useSelector((state) => state.mainSlice.isLoad);
  const partner = useSelector((state) => state.partnerStore?.partner || PARTNER_KEYS.NONE);

  const MEHUDET_USER_DATA_PARAM = "Meuhedetuserdata";
  const CLALIT_USER_DATA_PARAM = "userdata";
  const OVDIM_BARI_USER_DATA_PARAM = "Ovdimbariuserdata";

  const getUserDataFromUrl = () => {
    let searchParams = document.location.search;
    // Handle pages without query params such as orderHistory
    if (!searchParams) {
      const queryParams = localStorage.getItem("queryParams");
      if (!queryParams) {
        return null;
      }
      searchParams = queryParams;
    }
    localStorage.setItem("queryParams", searchParams); // Cache the query param in local storage
    //
    const [, queryString] = searchParams.split("?");
    let userDataParam = "";
    if (queryString.includes(MEHUDET_USER_DATA_PARAM)) {
      userDataParam = queryString.split(MEHUDET_USER_DATA_PARAM)[1];
      dispatch(setPartner(PARTNER_KEYS.MEHUDET));
    } else if (queryString.includes(OVDIM_BARI_USER_DATA_PARAM)) {
      userDataParam = queryString.split(OVDIM_BARI_USER_DATA_PARAM)[1];
      dispatch(setPartner(PARTNER_KEYS.OVDIM_BARI));
    } else if (queryString.includes(CLALIT_USER_DATA_PARAM)) {
      userDataParam = queryString.split(CLALIT_USER_DATA_PARAM)[1];
      dispatch(setPartner(PARTNER_KEYS.CLALIT));
    } else {
      dispatch(setPartner(PARTNER_KEYS.NONE));
    }
    return userDataParam ? userDataParam.slice(1) : "";
  };

  const handleUserLogin = async (userParams) => {
    const allUserParams = await userAPI.login({
      Token: userParams?.data.Token,
      ID: userParams?.data.ID,
      Phone: userParams?.data.Phone,
    });

    dispatch(updateUserData(allUserParams));

    const clubId = userParams.data.ClubId;
    if (clubId > 0) {
      navigate(`/venue/${clubId}`, {
        state: {
          price: "",
          clubId: clubId,
          clubName: "",
          ignoreApiCall: partner === PARTNER_KEYS.MEHUDET,
        },
      });
    }

    if (userParams?.data.ShowHealthConditionStatementForm) {
      navigate("/registrationQuestionnaire");
    }
  };

  const handlePhoneValidation = (userParams) => {
    navigate("/phone-validation", {
      state: {
        IsFreefitWeb: true,
        ClaimNumber: userParams.data.ClaimNumber,
        FirstName: userParams.data.FirstName,
        LastName: userParams.data.LastName,
        Email: userParams.data.Email,
        IsOrthoDox: userParams.data.IsOrthoDox,
        Gender: userParams.data.Gender,
        IsMinor: userParams.data?.IsMinor,
      },
    });
    dispatch(isLoadChanger(false));
  };

  const silentLogin = async () => {
    try {
      // Check if we're already on the partner error page to avoid infinite redirects
      if (location.pathname === "/partner-error") {
        dispatch(isLoadChanger(false));
        return;
      }

      const UserData = getUserDataFromUrl();

      if (!UserData) {
        dispatch(isLoadChanger(false));
        navigate("/partner-error");
        return;
      }

      // Check if no partner data is available and redirect to error page
      if (!localStorage.getItem("userToken")) {
        const searchParams = document.location.search;
        const hasValidPartnerParam =
          searchParams &&
          (searchParams.includes(MEHUDET_USER_DATA_PARAM) ||
            searchParams.includes(CLALIT_USER_DATA_PARAM) ||
            searchParams.includes(OVDIM_BARI_USER_DATA_PARAM));

        if (!hasValidPartnerParam) {
          dispatch(isLoadChanger(false));
          navigate("/partner-error");
          return;
        }
      }

      if (UserData) {
        localStorage.setItem("userToken", UserData);
      }

      dispatch(isLoadChanger(true));

      const userParams = await userAPI.getUserData(UserData || localStorage.getItem("userToken"));

      if (userParams.data?.IsOrthoDox != null) {
        dispatch(setIsOrthodox(userParams.data.IsOrthoDox));
      }

      if (userParams.data?.IsMinor != null) {
        dispatch(setIsMinor(userParams.data.IsMinor));
      }

      if (userParams.data.IsOtpRequired) {
        handlePhoneValidation(userParams);
        return;
      }

      if (userParams.data?.CorrelationId) {
        localStorage.setItem("CorrelationId", userParams.data?.CorrelationId);
      }

      dispatch(updateMainUserData({ ...userParams.data, Token: "12" + userParams.data.Token }));
      dispatch(updateDisplayingHealthForm(userParams.data.ShowHealthConditionStatementForm));
      dispatch(updateDisplayingTermOfUsePage(userParams.data.ShowTermOfUseFormConditionStatementForm));
      dispatch(updateUserToken(userParams.data.Token));

      if (userParams.data.Email === "") {
        navigate("/registration");
      } else {
        await handleUserLogin(userParams);
      }

      dispatch(isLoadChanger(false));
    } catch (err) {
      dispatch(isLoadChanger(false));

      // If we're already on the partner error page, don't show additional errors
      if (location.pathname === "/partner-error") {
        return;
      }

      // Check if the error is related to partner authentication
      // If no valid partner is detected and API fails, redirect to error page
      if (partner === PARTNER_KEYS.NONE) {
        navigate("/partner-error");
        return;
      }

      // For other errors, show the standard error dialog
      swalDialog(err);
    }
  };

  useEffect(() => {
    silentLogin();
  }, []);

  return (
    <>
      {isLoad ? <Loader /> : ""}
      <>{children}</>
    </>
  );
}
